import React, { useEffect, useMemo, useState } from 'react';
import Typography from '@/components/atoms/typography';
import AccountButton from './account-button';
import { AnnouncementBarProps } from './types';
import useTranslation from '@/providers/I18n/hooks/useTranslation';
import useAccount from '@/lib/hooks/useAccount';
import Modal from '../modal';
import Button from '@/components/atoms/button';
import Spinner from '../spinner';
import Link from 'next/link';
import { useRouter, usePathname } from 'next/navigation';
import HeaderLogo from '../header/header-logo';

const publicPages = [
  '/privacy',
  '/warranty',
  '/terms-of-use',
  '/disclaimer',
  '/collection-statement',
  '/contact-us',
  '/au/',
  '/nz/'
];

const AnnouncementBar = ({
  textBar,
  accountLinks,
  selectedBusinessUnit,
  businessUnits,
  selectedStore,
  stores,
  name,
  // quotes,
  onLogoutClick,
  onBusinessUnitChange,
  onStoreChange,
  logo,
  logoLink,
  isAdmin,
  country,
  topRightMenu
}: AnnouncementBarProps) => {

  const { translate } = useTranslation();
  const { loggedIn, account } = useAccount();
  const status = account?.onboarding_workflow_status === 'MemberRegistered';
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [newBusinessUnit, setNewBusinessUnit] = useState<string | null>(null);
  const [loading, setLoading] = useState(false)
  const closeModal = () => {
    setIsModalOpen(false);
  };

  // const handlePromoClose = () => {
  //   setIsPromoOpen(false);
  // };

  useEffect(() => {
    const body = document.body;
    if (isModalOpen) {
      body.style.overflow = 'hidden';
      body.classList.add('modal-open');
    } else {
      body.style.overflow = '';
      body.classList.remove('modal-open');
    }
    return () => {
      body.style.overflow = '';
      body.classList.remove('modal-open');
    };
  }, [isModalOpen]);

  const handleBusinessUnitChange = () => {
    if (newBusinessUnit && onBusinessUnitChange) {
      onBusinessUnitChange(newBusinessUnit);
      closeModal();
    }
  };

  const handleSelectChange = (value: string) => {
    if (value !== selectedBusinessUnit) {
      setNewBusinessUnit(value);
      if (businessUnits.length > 1) {
        setIsModalOpen(true);
      }
    }
  };
  const router = useRouter();
  const pathname = usePathname();
  const isProfileIncomplete = useMemo(() => {
    if (!loggedIn || !account) return false;

    const requiredFields = [
      account.firstName,
      account.lastName,
      account.email,
      account.companyName ?? account.company,
      account.phone,
      account.company_type,
      account.role,
      account.myrole,
      account.size_of_business,
      account.country,
      account.state,
    ];

    return requiredFields.some((field) => {
      if (Array.isArray(field)) return field.length === 0;
      if (field === null || field === undefined) return true;
      return String(field).trim().length === 0;
    });
  }, [account, loggedIn]);

 
  useEffect(() => {
    if (
      loggedIn &&
      !status &&
      pathname !== '/create-profile' &&
      publicPages.includes(pathname)
    ) {
      router.replace('/create-profile');
    }
  }, [loggedIn, status, router, pathname]);

  const isCreateProfilePage = pathname.includes("create-profile")
  return (

    <>
      {loading && <Spinner />}
      {loading && <div className="overlay fixed left-0 top-0 z-[1002] h-full w-full bg-gray-800 opacity-75"></div>}

      <div className="flex flex-col items-stretch">
        {isProfileIncomplete && (
          <div className="w-full bg-[#f8f6e8] px-4 py-2 text-center">
            <p className="text-[13px] font-medium">
              <b>Welcome to the new site! </b> We’ve made some improvements — please take a moment to <b>check and update your details</b> to keep everything running smoothly.{' '}
              <Link href="/profile-settings/" className="font-semibold text-black !underline">
                Click here!
              </Link>
            </p>
          </div>
        )}
        <div className={`mx-auto flex w-full flex-col items-stretch justify-center bg-white px-4 lg:px-10 max-w-[1920px]`}>
          <div className="hidden items-stretch justify-between gap-5 pt-4 max-md:max-w-full max-md:flex-wrap lg:flex">
            <HeaderLogo
              logo={logo}
              logoLink={(status) ? (logoLink.href || '/') : isCreateProfilePage ? undefined : logoLink.href}
              imageClassName=" w-[310px] h-[64px] flex text-16 font-400 text-[16px] leading-[24px]"
            />

            <div className={`my-auto flex items-stretch justify-start gap-x-20 self-center `}>

              <div className="flex items-center justify-center gap-[0.5rem]">
                {status && (
                  <div className="flex gap-4">
                    {topRightMenu?.map((menuItem: any, idx: number) =>
                      menuItem?.menu_links?.title === "Trade" ? (
                        <Link
                          key={idx}
                          href={menuItem?.menu_links?.href}
                          target="_blank"
                        >
                          <Typography className="text-14 leading-normal font-semibold p-2 text-neutral-600">
                            {menuItem?.menu_links?.title}
                          </Typography>
                        </Link>
                      ) : null
                    )}
                  </div>
                )}
                {loggedIn && status ? (
                  <AccountButton
                    // quotes={quotes}
                    name={name}
                    onLogoutClick={onLogoutClick}
                    accountLinks={accountLinks}
                    selectedBusinessUnit={selectedBusinessUnit}
                    businessUnits={businessUnits}
                    onBusinessUnitChange={onBusinessUnitChange}
                    selectedStore={selectedStore}
                    stores={stores}
                    onStoreChange={onStoreChange}
                    setLoading={setLoading}
                    isAdmin={isAdmin}
                  />
                ) : null}
              </div>
            </div>
          </div>
          {/* <div className="flex justify-between lg:hidden">
            <HeaderLogo
              logo={logo}
              logoLink={status ? (logoLink.href || '/') : undefined}
              imageClassName=" flex h-[38px] w-[154px] text-16 font-400 text-[16px] leading-[24px]"
            />
            <div className={`my-auto flex items-stretch justify-start gap-x-20 self-center `}>
              {status && (
                <div className="flex items-center justify-center gap-[0.5rem]">
                  <div className='flex gap-4' >
                    {
                      topRightMenu?.map((menuItem: any, idx: number) => {
                        return (
                          <React.Fragment key={idx}>
                            <Link href={`${menuItem?.menu_links.href}`} target='_blank'>
                              <Typography className="text-14 leading-normal font-semibold break-words    py-2 text-[#525252]">
                                {menuItem?.menu_links?.title}
                              </Typography>
                            </Link>
                          </React.Fragment>
                        )
                      })
                    }
                  </div>
                </div>
              )}
            </div>
          </div> */}
        </div>
        <Modal
          className="flex flex-col items-center justify-center !p-10"
          isOpen={isModalOpen}
          onRequestClose={closeModal}
          preventScroll={false}
          closeButton={true}
          size="sm"
          variant="default"
          centered={true}
        >
          <div className="div">
            <Typography className="text-center text-16 !font-medium text-black">
              {translate('common.switch.company')}
            </Typography >
            <Typography className=" mt-4 text-center text-16 !font-medium text-black">{translate('common.switch.details')}</Typography>
            <Typography className=" mt-4 text-center text-16 !font-medium text-black">{translate('common.switch.buconfirm')}</Typography>
          </div>
          <div className="flex gap-4">
            <Button
              size="custom"
              variant="secondary"
              type="submit"
              className="mt-5 bg-[#2B2C39] !py-3 px-6 text-white"
              onClick={handleBusinessUnitChange}
            >
              {translate('common.yes')}
            </Button>
            <Button
              size="custom"
              variant="secondary"
              type="submit"
              className="mt-5 bg-[#2B2C39] !py-3 px-6 text-white"
              onClick={() => {
                setIsModalOpen(false);
              }}
            >
              {translate('common.no')}
            </Button>
          </div>
        </Modal>
      </div>
    </>
  );
};

export default AnnouncementBar;
